<template>
  <v-container id="productDetail" :key="productId" fluid class="pa-0">
    <CategoryBanner />
    <v-row>
      <GoToBanner v-if="product.category_id" path="CategoryPage" :params="{ categoryId: product.category_id }" />
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-carousel v-if="productImages.length > 0" hide-delimiter-background :show-arrows="productImages.length > 1" show-arrows-on-hover>
          <v-carousel-item v-for="image in productImages" :key="image.src" reverse-transition="fade-transition" transition="fade-transition">
            <v-img v-if="image.src" :key="image.src" :src="image.src" alt="img" max-height="400" contain>
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-img :src="require('@/assets/img-placeholder.png')" />
                </v-row>
              </template>
            </v-img>
            <v-img v-else :key="require('@/assets/img-placeholder.png')" :src="image.src" alt="img" max-height="400" contain />
          </v-carousel-item>
        </v-carousel>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-center justify-md-start text-center text-md-left">
        <v-card tile flat>
          <v-card-title class="body-heading-large">
            {{ product.name }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="12">
                <p class="secondary--text font-weight-bold body-heading-medium">{{ product.points }} points</p>
              </v-col>
              <v-col v-if="isKid" cols="12" md="12">
                <v-btn color="secondary" @click="addToCart">
                  <v-icon left size="16">fas fa-shopping-cart</v-icon>
                  Add to Cart
                </v-btn>
                <v-btn @click.native.stop.prevent="toggleItemFromWishList" depressed class="ma-2">
                  <v-icon color="secondary">{{ btnIcon }}</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <WishListSection v-if="isKid" />
    <RemoveItemFromWishListModal v-if="isKid && wishlistItem" :key="wishlistItem.id" :isModalOpen="isRemoveItemFromWishListModalOpen" :itemId="wishlistItem.id" @close-modal="toggleRemoveItemFromWishListModal" />
    <!-- Modal to show request result -->
    <ResultModal v-if="isKid" :isModalOpen="isResultModalOpen" :success="isRequestSuccess" :successMsgTitle="requestResultMsg" :successActions="requestSuccessActions" :failedActions="requestFailedActions" />
  </v-container>
</template>

<script>
  import GoToBanner from "@/components/GoToBanner";
  import RemoveItemFromWishListModal from "@/components/WishList/RemoveItemFromWishListModal";
  import ResultModal from "@/components/ResultModal";
  import WishListSection from "@/components/WishList/WishListSection";
  import CategoryBanner from "@/components/Catalog/CategoryBanner";

  export default {
    name: "ProductDetail",

    data() {
      return {
        isRequestSuccess: false,
        requestResultMsg: "",
        requestSuccessActions: [
          {
            name: "View Cart",
            event: this.goToCartOverview,
          },
          {
            name: "Continue Shopping",
            event: this.closeResultModal,
          },
        ],
        requestFailedActions: [
          {
            name: "Done",
            event: this.closeResultModal,
          },
        ],
        isResultModalOpen: false,
        isRemoveItemFromWishListModalOpen: false,
        product: {},
        productImages: [],
      };
    },

    watch: {
      productId: "reload",
    },

    computed: {
      isKid() {
        return this.$store.getters.userInfo.role === "child";
      },
      productId() {
        return this.$route.params.id;
      },
      wishlistItem() {
        return this.$store.getters.findWishByProductId(this.productId);
      },
      isItemInWishList() {
        return !this.$validator.isEmpty(this.wishlistItem);
      },
      btnIcon() {
        return this.isItemInWishList ? "mdi-heart" : "mdi-heart-outline";
      },
    },

    components: { GoToBanner, CategoryBanner, RemoveItemFromWishListModal, ResultModal, WishListSection },

    methods: {
      goToCartOverview() {
        if (this.$route.name !== "CartOverview") {
          this.$router.push({
            name: "CartOverview",
          });
        }
      },

      closeResultModal() {
        this.isResultModalOpen = false;
      },

      addToCart() {
        this.$store.commit("setLoadingStatus", true);

        this.$http
          .post("/cart", { product_id: this.productId })
          .then((response) => {
            if (response && response.data) {
              this.addToCartSuccess();
            } else {
              this.$store.commit("setError", {
                message: this.$formatter.getErrorMsg(),
                type: "error",
              });
              this.$store.commit("setLoadingStatus", false);
            }
          })
          .catch((error) => {
            this.addToCartFail(error);
          });
      },

      addToCartSuccess() {
        this.isRequestSuccess = true;
        this.requestResultMsg = "1 item added to your cart";

        this.isResultModalOpen = true;
        this.$store.commit("setLoadingStatus", false);
      },

      addToCartFail(error) {
        console.log(error);
        this.requestResultMsg = "Opps! Looks like something went wrong, please try again later.";
        this.isRequestSuccess = false;
        this.isResultModalOpen = true;
        this.$store.commit("setLoadingStatus", false);
      },

      toggleRemoveItemFromWishListModal() {
        this.isRemoveItemFromWishListModalOpen = !this.isRemoveItemFromWishListModalOpen;
      },

      reload() {
        this.$router.go();
      },

      addWish() {
        this.$store.commit("setLoadingStatus", true);

        this.$http
          .post("/wishes", { wish: { product_id: this.productId } })
          .then((response) => {
            this.$store.commit("addWish", response.data.wish);

            this.$store.commit("setError", {
              message: "Added the product to your wish list",
              type: "success",
            });
            this.$store.commit("setLoadingStatus", false);
          })
          .catch((error) => {
            console.error(error);
            this.$store.commit("setError", {
              message: "Failed to add the product to your wish list",
              type: "error",
            });
            this.$store.commit("setLoadingStatus", false);
          });
      },

      toggleItemFromWishList() {
        this.isItemInWishList ? this.toggleRemoveItemFromWishListModal() : this.addWish();
      },

      fetchProduct() {
        this.$store.commit("setLoadingStatus", true);

        this.$http
          .get(`/products/${this.productId}`)
          .then((response) => {
            this.product = response.data.product;
            this.productImages.push({ src: response.data.product.image });
            this.$store.commit("setLoadingStatus", false);
          })
          .catch((error) => {
            console.error(error);
            this.$store.commit("setError", {
              message: "Failed to retrieve product information",
              type: "error",
            });
            this.$store.commit("setLoadingStatus", false);
          });
      },
    },

    created() {
      this.fetchProduct();
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/Products/ProductDetail.vue